import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CostLedgerView,
  CostRecoveryCaseView,
  CostRecoverySettingsView,
  CreateCostItemDto,
  CreatePaymentPlanDto,
  EscalateDto,
  GenerateCostDocumentDto,
  InvoiceReading,
  IssueStatementDto,
  RecordPaymentDto,
  ReviewCostItemDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { apiErrorStatus, useMutationToast } from './mutation-toast';
import { formsKeys } from './forms-api';

/**
 * Cost recovery: a file's account, the municipality's ledger and switch.
 *
 * Every write answers with the whole account, so a mutation replaces the cached
 * view rather than invalidating and re-fetching it — the balance on screen is
 * always the one the server just computed.
 */
export const costKeys = {
  file: (fileId: string) => ['cost-recovery', 'file', fileId] as const,
  ledger: (localGovernmentId: string) => ['cost-recovery', 'ledger', localGovernmentId] as const,
  settings: (localGovernmentId: string) =>
    ['cost-recovery', 'settings', localGovernmentId] as const,
};

const base = (fileId: string) => `/files/${fileId}/cost-recovery`;

/**
 * A file's account. A 404 (cost recovery off here) or a 403 (not for this
 * account) is an answer, not an error to retry: the panels that ask simply do
 * not appear.
 */
export function useCostRecovery(fileId: string | undefined) {
  return useQuery({
    queryKey: costKeys.file(fileId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<CostRecoveryCaseView>(base(fileId as string));
      return data;
    },
    enabled: !!fileId,
    retry: (count, err) => {
      const status = apiErrorStatus(err);
      return status !== 404 && status !== 403 && count < 2;
    },
  });
}

export function useCostSettings(localGovernmentId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: costKeys.settings(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<CostRecoverySettingsView>(
        `/cost-recovery/local-governments/${localGovernmentId}/settings`,
      );
      return data;
    },
    enabled: !!localGovernmentId && enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useCostLedger(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: costKeys.ledger(localGovernmentId ?? ''),
    queryFn: async () => {
      const { data } = await api.get<CostLedgerView>(
        `/cost-recovery/local-governments/${localGovernmentId}/ledger`,
      );
      return data;
    },
    enabled: !!localGovernmentId,
    retry: (count, err) => apiErrorStatus(err) !== 404 && count < 2,
  });
}

/** One write against the file's account; the response replaces the cached view. */
function useCostWrite<V>(
  fileId: string,
  request: (vars: V) => Promise<CostRecoveryCaseView>,
  successKey: string,
  errorKey: string,
  /** Anything else the write changed, outside the account. */
  alsoChanged: readonly (readonly unknown[])[] = [],
) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      qc.setQueryData(costKeys.file(fileId), data);
      if (data.localGovernmentId) {
        void qc.invalidateQueries({ queryKey: costKeys.ledger(data.localGovernmentId) });
      }
      for (const queryKey of alsoChanged) void qc.invalidateQueries({ queryKey });
      toast.success(successKey);
    },
    onError: (err) => toast.error(err, errorKey),
  });
}

const post = async <B>(url: string, body?: B) =>
  (await api.post<CostRecoveryCaseView>(url, body ?? {})).data;

function upload(url: string, field: string, file: File) {
  const form = new FormData();
  form.append(field, file);
  return api.post<CostRecoveryCaseView>(url, form).then((r) => r.data);
}

/**
 * The row a write added: the one the account did not have before it. Picked by
 * difference rather than by date, so a colleague's cost recorded in the same
 * second cannot receive this receipt.
 */
function added<T extends { id: string }>(before: readonly T[], after: readonly T[]): T | undefined {
  const known = new Set(before.map((r) => r.id));
  return after.find((r) => !known.has(r.id));
}

export function useCreateCostItem(fileId: string) {
  const qc = useQueryClient();
  return useCostWrite(
    fileId,
    async ({ dto, receipt }: { dto: CreateCostItemDto; receipt?: File | null }) => {
      const before = qc.getQueryData<CostRecoveryCaseView>(costKeys.file(fileId))?.items ?? [];
      const view = await post(`${base(fileId)}/items`, dto);
      const created = receipt ? added(before, view.items) : undefined;
      return created && receipt
        ? upload(`${base(fileId)}/items/${created.id}/receipts`, 'receipt', receipt)
        : view;
    },
    'gov.costRecovery.toast.recorded',
    'gov.costRecovery.toast.recordError',
  );
}

export function useReviewCostItem(fileId: string) {
  return useCostWrite(
    fileId,
    ({ itemId, dto }: { itemId: string; dto: ReviewCostItemDto }) =>
      post(`${base(fileId)}/items/${itemId}/review`, dto),
    'gov.costRecovery.toast.reviewed',
    'gov.costRecovery.toast.reviewError',
  );
}

export function useVoidCostItem(fileId: string) {
  return useCostWrite(
    fileId,
    ({ itemId, reason }: { itemId: string; reason: string }) =>
      post(`${base(fileId)}/items/${itemId}/void`, { reason }),
    'gov.costRecovery.toast.voided',
    'gov.costRecovery.toast.voidError',
  );
}

export function useAddItemReceipt(fileId: string) {
  return useCostWrite(
    fileId,
    ({ itemId, file }: { itemId: string; file: File }) =>
      upload(`${base(fileId)}/items/${itemId}/receipts`, 'receipt', file),
    'gov.costRecovery.toast.receiptAdded',
    'gov.costRecovery.toast.receiptError',
  );
}

/** Reads an invoice PDF and suggests its figures. Stores nothing. */
export async function readInvoice(fileId: string, file: File): Promise<InvoiceReading> {
  const form = new FormData();
  form.append('invoice', file);
  const { data } = await api.post<InvoiceReading>(`${base(fileId)}/read-invoice`, form);
  return data;
}

export function useIssueStatement(fileId: string) {
  return useCostWrite(
    fileId,
    (dto: IssueStatementDto) => post(`${base(fileId)}/statements`, dto),
    'gov.costRecovery.toast.statementIssued',
    'gov.costRecovery.toast.statementError',
  );
}

export function useGenerateCostDocument(fileId: string) {
  return useCostWrite(
    fileId,
    (dto: GenerateCostDocumentDto) => post(`${base(fileId)}/documents`, dto),
    'gov.costRecovery.toast.documentGenerated',
    'gov.costRecovery.toast.documentError',
  );
}

export function useRecordPayment(fileId: string) {
  const qc = useQueryClient();
  return useCostWrite(
    fileId,
    async ({ dto, receipt }: { dto: RecordPaymentDto; receipt?: File | null }) => {
      const before = qc.getQueryData<CostRecoveryCaseView>(costKeys.file(fileId))?.payments ?? [];
      const view = await post(`${base(fileId)}/payments`, dto);
      const created = receipt ? added(before, view.payments) : undefined;
      return created && receipt
        ? upload(`${base(fileId)}/payments/${created.id}/receipts`, 'receipt', receipt)
        : view;
    },
    'gov.costRecovery.toast.paymentRecorded',
    'gov.costRecovery.toast.paymentError',
  );
}

export function useVoidPayment(fileId: string) {
  return useCostWrite(
    fileId,
    ({ paymentId, reason }: { paymentId: string; reason: string }) =>
      post(`${base(fileId)}/payments/${paymentId}/void`, { reason }),
    'gov.costRecovery.toast.paymentVoided',
    'gov.costRecovery.toast.paymentError',
  );
}

export function useCreatePlan(fileId: string) {
  return useCostWrite(
    fileId,
    (dto: CreatePaymentPlanDto) => post(`${base(fileId)}/plan`, dto),
    'gov.costRecovery.toast.planCreated',
    'gov.costRecovery.toast.planError',
  );
}

export function useCancelPlan(fileId: string) {
  return useCostWrite(
    fileId,
    async () => (await api.delete<CostRecoveryCaseView>(`${base(fileId)}/plan`)).data,
    'gov.costRecovery.toast.planCancelled',
    'gov.costRecovery.toast.planError',
  );
}

export function useEscalate(fileId: string, localGovernmentId: string | null) {
  return useCostWrite(
    fileId,
    (dto: EscalateDto) =>
      post(`/cost-recovery/local-governments/${localGovernmentId}/files/${fileId}/escalate`, dto),
    'gov.costRecovery.toast.escalated',
    'gov.costRecovery.toast.escalateError',
    // A tax-roll escalation issues the reimbursement request as a file document.
    [formsKeys.forFile(fileId)],
  );
}
